import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { loadDefaultLocationRadius } from '../utils/defaultLocationRadius';
import { saveDemoPledge } from '../utils/demoPledgeStorage';
import { getCompactAddressLabel } from '../utils/addressLabels';
import { isPersonalApp, isPublicApp } from '../utils/appMode';
import {
  getPublicLocationLabelError,
  getSafePlaceNameSuggestion,
  normalizePublicLocationLabel
} from '../utils/publicLocationLabel';
import {
  createFulfillmentSecret,
  hashFulfillmentSecret,
  loadPublicSavedLocations,
  savePublicSavedLocation,
  savePublicPledgeTarget
} from '../utils/publicPledgeStorage';
import placePledgeLogo from '../assets/place-pledge-logo.svg';
import { BrandHeaderRow, HeaderIconButton, HomeIcon } from '../components/PageTheme';

const Page = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem;
  background:
    radial-gradient(circle at 15% 5%, rgba(251, 191, 36, 0.28), transparent 28rem),
    radial-gradient(circle at 90% 15%, rgba(96, 165, 250, 0.34), transparent 24rem),
    linear-gradient(145deg, #101827 0%, #111827 42%, #1f2937 100%);
  color: #f8fafc;

  @media (max-width: 640px) {
    padding: 0.95rem 0.875rem 2rem;
  }
`;

const Shell = styled.div`
  width: min(920px, 100%);
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    margin-bottom: 0.55rem;

    ${BrandHeaderRow} {
      margin-bottom: 0;
    }
  }
`;

const HeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const BrandLogo = styled.img`
  display: block;
  width: min(260px, 72vw);
  height: auto;
  margin-bottom: 0.85rem;

  @media (max-width: 640px) {
    margin-bottom: 0;
  }

  @media (max-width: 420px) {
    width: min(230px, 76vw);
  }
`;

const Card = styled.form`
  position: relative;
  display: grid;
  gap: 1.25rem;
  padding: 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 32px;
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(18px);
`;

const MenuScrim = styled.button`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  border: 0;
  background: rgba(2, 6, 23, 0.42);
  backdrop-filter: blur(2px);
  cursor: default;
`;

const TimeGrid = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;

  @media (max-width: 520px) {
    gap: 0.65rem;
  }
`;

const Field = styled.label`
  display: inline-flex;
  color: #cbd5e1;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const VisuallyHiddenText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const PledgePanel = styled.section`
  padding: clamp(1.25rem, 4vw, 2rem);
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(251, 191, 36, 0.14)),
    rgba(255, 255, 255, 0.06);
`;

const PledgeText = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  color: #f8fafc;
  font-size: clamp(1.55rem, 5.8vw, 3.25rem);
  font-weight: 950;
  line-height: 1.18;
  letter-spacing: -0.06em;
  overflow: visible;
`;

const PledgeWords = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

const TimeSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  color: #f8fafc;
  font-size: clamp(1.55rem, 5.8vw, 3.25rem);
  font-weight: 950;
  line-height: 1;
  letter-spacing: -0.06em;
  text-transform: none;
`;

const InlineControl = styled.div`
  display: inline-flex;
  min-width: 0;
`;

const Input = styled.input`
  width: auto;
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font-size: 1.1rem;
  font-weight: 900;

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const Select = styled.select`
  width: auto;
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.9rem 2.8rem 0.9rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font-size: 1.05rem;
  font-weight: 900;
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #fbbf24 50%),
    linear-gradient(135deg, #fbbf24 50%, transparent 50%);
  background-position:
    calc(100% - 20px) 50%,
    calc(100% - 14px) 50%;
  background-size:
    6px 6px,
    6px 6px;
  background-repeat: no-repeat;

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const SentenceSelect = styled(Select)`
  width: var(--select-width, auto);
  max-width: min(100%, 30rem);
  min-width: 0;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  border-radius: 999px;

  @media (max-width: 520px) {
    width: 100%;
    min-width: 0;
  }
`;

const RecipientPicker = styled.div`
  position: relative;
  display: inline-grid;
  min-width: 0;
  z-index: ${({ $active }) => ($active ? 40 : 1)};
`;

const RecipientButton = styled.button`
  width: var(--select-width, auto);
  max-width: min(100%, 30rem);
  min-width: 0;
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.9rem 2.8rem 0.9rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 999px;
  background:
    linear-gradient(45deg, transparent 50%, #fbbf24 50%) calc(100% - 20px) 50% / 6px 6px no-repeat,
    linear-gradient(135deg, #fbbf24 50%, transparent 50%) calc(100% - 14px) 50% / 6px 6px no-repeat,
    rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font: inherit;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  font-weight: 900;
  text-align: left;
  cursor: pointer;

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const RecipientMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  display: ${({ $open }) => ($open ? 'grid' : 'none')};
  width: max(100%, 14rem);
  max-width: calc(100vw - 2rem);
  padding: 0.45rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.98);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
`;

const RecipientOption = styled.button`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 0;
  border-top: ${({ $manage }) => ($manage ? '1px solid rgba(226, 232, 240, 0.14)' : '0')};
  border-radius: 13px;
  margin-top: ${({ $manage }) => ($manage ? '0.35rem' : '0')};
  background: transparent;
  color: ${({ $manage }) => ($manage ? '#fbbf24' : '#f8fafc')};
  font: inherit;
  font-size: 1rem;
  font-weight: 900;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.08);
    outline: none;
  }
`;

const SentenceInput = styled(Input)`
  min-width: 10.5rem;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  border-radius: 999px;

  @media (max-width: 520px) {
    width: 100%;
    min-width: 0;
  }
`;

const DemoRecipientChip = styled.span`
  display: inline-flex;
  align-items: center;
  width: auto;
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.85rem 1.05rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.46);
  color: #fbbf24;
  font-size: clamp(0.9rem, 2.5vw, 1.15rem);
  font-weight: 950;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
`;

const SenderNameInput = styled(SentenceInput)`
  width: min(100%, 18rem);
  min-width: min(12rem, 100%);
`;

const DateInputShell = styled.span`
  position: relative;
  display: inline-flex;
  min-width: 10.5rem;

  @media (max-width: 520px) {
    width: 100%;
    min-width: 0;
  }
`;

const DateDisplay = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.9rem 1.25rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  font-weight: 900;
  line-height: 1.35;
  pointer-events: none;
`;

const DatePickerInput = styled(SentenceInput)`
  position: absolute;
  inset: 0;
  width: 100%;
  min-width: 0;
  opacity: 0;
  cursor: pointer;
`;

const MoneyRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

const AmountSlider = styled.div`
  display: grid;
  gap: 0.35rem;
  width: clamp(13rem, 34vw, 20rem);
  min-height: 58px;
  box-sizing: border-box;
  padding: 0.55rem 0.9rem 0.65rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.7);

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const AmountSliderHeader = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  color: #cbd5e1;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const AmountSliderValue = styled.span`
  color: #f8fafc;
  font-size: 1.4rem;
  font-weight: 950;
  letter-spacing: 0;
  line-height: 1;
  text-transform: none;
  white-space: nowrap;
`;

const RangeInput = styled.input`
  width: 100%;
  height: 30px;
  margin: 0;
  accent-color: #fbbf24;
  appearance: none;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 7px;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.18);
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 22px;
    height: 22px;
    margin-top: -7.5px;
    border: 2px solid #111827;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
  }

  &::-moz-range-track {
    height: 7px;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.18);
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border: 2px solid #111827;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
  }

  &:focus-visible {
    outline: 3px solid rgba(251, 191, 36, 0.32);
    outline-offset: 6px;
    border-radius: 999px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 118px;
  box-sizing: border-box;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  font-size: 1rem;
  resize: vertical;

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const SentenceTextArea = styled(TextArea)`
  width: min(100%, 38rem);
  min-height: 92px;
  box-sizing: border-box;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  font-weight: 800;
  line-height: 1.2;
  resize: vertical;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const MessagePreviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: min(100%, 31rem);
  min-height: 58px;
  padding: 0.85rem 1.1rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  color: ${({ $empty }) => ($empty ? 'rgba(248, 250, 252, 0.58)' : '#f8fafc')};
  font: inherit;
  font-size: clamp(1.1rem, 3vw, 1.6rem);
  font-weight: 900;
  line-height: 1.15;
  text-align: left;
  cursor: pointer;

  span {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }

  @media (max-width: 520px) {
    width: 100%;
    min-height: 54px;
    padding: 0.75rem 1rem;
  }
`;

const MessagePhrase = styled.span`
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  max-width: 100%;

  ${MessagePreviewButton} {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 640px) {
    gap: 0.45rem;

    ${MessagePreviewButton} {
      min-height: 50px;
      padding: 0.7rem 0.95rem;
      font-size: clamp(0.98rem, 4.2vw, 1.28rem);
    }
  }
`;

const SubmitButton = styled.button`
  min-height: 62px;
  border: 0;
  border-radius: 22px;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  color: #111827;
  font-size: 1.15rem;
  font-weight: 950;
  cursor: pointer;
  box-shadow: 0 18px 35px rgba(251, 146, 60, 0.28);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const FormActions = styled.div`
  display: grid;
  grid-template-columns: minmax(14rem, 0.7fr) max-content;
  gap: 0.85rem;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewButton = styled.button`
  min-height: 62px;
  padding: 0 1.55rem;
  border: 1px solid rgba(251, 191, 36, 0.5);
  border-radius: 22px;
  background: rgba(251, 191, 36, 0.08);
  color: #fde68a;
  font-size: 1.05rem;
  font-weight: 950;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 640px) {
    width: 100%;
    white-space: normal;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid rgba(248, 113, 113, 0.45);
  border-radius: 16px;
  background: rgba(127, 29, 29, 0.45);
  color: #fecaca;
  font-weight: 800;
`;

const SuccessMessage = styled(ErrorMessage)`
  border-color: rgba(52, 211, 153, 0.45);
  background: rgba(6, 95, 70, 0.42);
  color: #bbf7d0;
`;

const MessageModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(4px);
`;

const MessageModal = styled.div`
  width: min(560px, 100%);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  padding: 1rem 1rem 0;
  border: 1px solid rgba(226, 232, 240, 0.2);
  border-radius: 24px;
  background: #0f172a;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.45);

  @media (max-width: 520px) {
    max-height: calc(100dvh - 1rem);
    padding: 0.75rem 0.75rem 0;
    border-radius: 20px;
  }
`;

const MessageModalTitle = styled.h2`
  margin: 0 0 0.85rem;
  color: #f8fafc;
  font-size: 1.25rem;
  font-weight: 950;
  line-height: 1.1;
`;

const MessageModalActions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 0.85rem;
  padding: 0.75rem 0 1rem;
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.72), #0f172a 28%);

  @media (max-width: 520px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-bottom: 0.75rem;
  }

  > button:only-child {
    grid-column: 1 / -1;
  }
`;

const EmailPreviewShell = styled.div`
  display: grid;
  gap: 0.8rem;

  @media (max-width: 520px) {
    gap: 0.65rem;
  }
`;

const EmailPreviewMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.62);
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;

  strong {
    color: #f8fafc;
  }
`;

const EmailPreviewCard = styled.div`
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 16px;
  background:
    linear-gradient(180deg, rgba(30, 41, 59, 0.96), rgba(15, 23, 42, 0.96)),
    #111827;
  color: #e2e8f0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const EmailPreviewHeader = styled.div`
  padding: 1.35rem 1.2rem 0.8rem;

  @media (max-width: 520px) {
    padding: 1rem 1rem 0.55rem;
  }
`;

const EmailPreviewBrand = styled.div`
  color: #fbbf24;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const EmailPreviewHeadline = styled.h3`
  margin: 0.55rem 0 0;
  color: #f8fafc;
  font-size: clamp(1.45rem, 5vw, 1.9rem);
  font-weight: 950;
  line-height: 1.16;

  @media (max-width: 520px) {
    font-size: 1.45rem;
  }
`;

const EmailPreviewBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 0 1.2rem 1.35rem;
  color: #cbd5e1;
  font-size: 0.98rem;
  line-height: 1.55;

  p {
    margin: 0;
  }

  @media (max-width: 520px) {
    gap: 0.75rem;
    padding: 0 1rem 1rem;
    font-size: 0.92rem;
    line-height: 1.45;
  }
`;

const EmailPreviewBlock = styled.div`
  display: grid;
  gap: 0.35rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.12);

  @media (max-width: 520px) {
    padding-bottom: 0.75rem;
  }
`;

const EmailPreviewLabel = styled.div`
  color: #93c5fd;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const EmailPreviewPrimary = styled.div`
  color: #f8fafc;
  font-size: 1.12rem;
  font-weight: 900;
  line-height: 1.45;
`;

const EmailPreviewMessage = styled.div`
  color: #f8fafc;
  font-size: clamp(1.25rem, 4.5vw, 1.55rem);
  font-weight: 950;
  line-height: 1.35;
  white-space: pre-wrap;

  @media (max-width: 520px) {
    font-size: 1.2rem;
  }
`;

const EmailPreviewFooter = styled.div`
  padding: 0.85rem 1.2rem;
  border-top: 1px solid rgba(226, 232, 240, 0.12);
  background: rgba(2, 6, 23, 0.38);
  color: #94a3b8;
  font-size: 0.78rem;
  line-height: 1.45;

  @media (max-width: 520px) {
    padding: 0.7rem 1rem;
    font-size: 0.72rem;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: #fbbf24;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #cbd5e1;
  font-weight: 800;
`;

const MapModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(4px);
`;

const MapModal = styled.div`
  position: relative;
  width: min(920px, 100%);
  height: min(74vh, 640px);
  min-height: 420px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.2);
  border-radius: 28px;
  background: #0f172a;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.45);

  @media (max-width: 640px) {
    height: 78vh;
    min-height: 420px;
    border-radius: 22px;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const MapOverlayActions = styled.div`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.32);

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const MapSelectionDetails = styled.div`
  display: grid;
  gap: 0.35rem;
  min-width: 0;
`;

const SelectedAddress = styled.div`
  max-width: min(540px, 100%);
  color: #f8fafc;
  font-size: 0.92rem;
  font-weight: 900;
  line-height: 1.28;
`;

const PublicLocationLabelField = styled.label`
  display: grid;
  gap: 0.35rem;
  width: min(360px, 100%);
`;

const PublicLocationLabelText = styled.span`
  color: #f8fafc;
  font-size: 0.82rem;
  font-weight: 950;
`;

const PublicLocationLabelInput = styled.input`
  width: 100%;
  min-height: 42px;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.76);
  color: #f8fafc;
  font: inherit;
  font-weight: 850;

  &::placeholder {
    color: rgba(226, 232, 240, 0.54);
  }

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const PublicLocationLabelHint = styled.span`
  color: rgba(226, 232, 240, 0.68);
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.3;
`;

const MapSearchPanel = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  right: 1rem;
  z-index: 2;
  display: grid;
  gap: 0.45rem;
  max-width: min(560px, calc(100% - 2rem));
`;

const MapSearchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.5rem;
  padding: 0.45rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.28);

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }

  > div {
    min-width: 0;
  }
`;

const MapSearchInput = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 42px;
  box-sizing: border-box;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.76);
  color: #f8fafc;
  font: inherit;
  font-weight: 850;

  &::placeholder {
    color: rgba(226, 232, 240, 0.58);
  }

  &:focus {
    border-color: #fbbf24;
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.16);
    outline: none;
  }
`;

const MapSearchButton = styled.button`
  min-height: 42px;
  padding-inline: 1.15rem;
  border: 1px solid rgba(251, 191, 36, 0.55);
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  color: #111827;
  font: inherit;
  font-weight: 950;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const MapSearchError = styled.div`
  width: fit-content;
  max-width: 100%;
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(248, 113, 113, 0.38);
  border-radius: 14px;
  background: rgba(127, 29, 29, 0.72);
  color: #fecaca;
  font-size: 0.88rem;
  font-weight: 850;
`;

const SaveForLaterLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #f8fafc;
  font-size: 0.95rem;
  font-weight: 900;

  input {
    width: 22px;
    height: 22px;
    accent-color: #fbbf24;
  }
`;

const SaveForLaterField = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const SaveForLaterHint = styled.span`
  color: rgba(226, 232, 240, 0.66);
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.3;
`;

const MapActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;

  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const MapButton = styled.button`
  min-height: 44px;
  padding: 0.65rem 1rem;
  border: 1px solid ${({ $secondary }) => ($secondary ? 'rgba(226, 232, 240, 0.18)' : 'rgba(251, 191, 36, 0.55)')};
  border-radius: 999px;
  background: ${({ $secondary }) => ($secondary ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #fbbf24, #fb923c)')};
  color: ${({ $secondary }) => ($secondary ? '#f8fafc' : '#111827')};
  font: inherit;
  font-weight: 950;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const MapHint = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 2;
  max-width: min(420px, calc(100% - 2rem));
  padding: 0.65rem 0.8rem;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.9);
  color: rgba(226, 232, 240, 0.9);
  font-weight: 850;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.28);
`;

const MapLocateButton = styled.button`
  position: absolute;
  top: 5.75rem;
  right: 1rem;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.92);
  color: #f8fafc;
  cursor: pointer;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.28);

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.16s ease;
  }

  &:hover svg,
  &:focus-visible svg {
    transform: rotate(18deg) scale(1.04);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    top: 6rem;
  }

  @media (max-width: 520px) {
    top: 8.85rem;
  }
`;

const CompassIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M15.7 6.9 13.4 13.4 6.9 15.7 9.2 9.2z" fill="#fbbf24" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="1.35" fill="currentColor" />
  </svg>
);

const formatDateInputValue = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDateDisplayLabel = (dateValue) => {
  if (!dateValue) {
    return 'Choose date';
  }

  const [year, month, day] = dateValue.split('-').map(Number);
  const selectedDate = new Date(year, month - 1, day);

  if (Number.isNaN(selectedDate.getTime())) {
    return dateValue;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (selectedDate.getTime() === today.getTime()) {
    return 'Today';
  }

  if (selectedDate.getTime() === tomorrow.getTime()) {
    return 'TMRW';
  }

  return selectedDate.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: selectedDate.getFullYear() === today.getFullYear() ? undefined : 'numeric'
  });
};

const getAlarmSortTime = (alarm) => {
  const createdAt = new Date(alarm.createdAt).getTime();

  if (!Number.isNaN(createdAt)) {
    return createdAt;
  }

  const alarmTime = new Date(alarm.time).getTime();

  return Number.isNaN(alarmTime) ? 0 : alarmTime;
};

const getMostRecentAlarm = (alarms) => {
  return [...alarms].sort((firstAlarm, secondAlarm) => {
    return getAlarmSortTime(secondAlarm) - getAlarmSortTime(firstAlarm);
  })[0];
};

const isUserSavedLocation = (location) => {
  return location?.temporary !== true && location?.source !== 'run_pledge';
};

const AMOUNT_MIN = 1;
const AMOUNT_MAX = 50;
const DEFAULT_AMOUNT = '5';
const GUEST_SAMPLE_RECIPIENT_EMAIL = 'friend@demo.com';
const GOOGLE_MAPS_LIBRARIES = ['places'];

const formatAmountInput = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return DEFAULT_AMOUNT;
  }

  return String(Math.min(AMOUNT_MAX, Math.max(AMOUNT_MIN, Math.round(number))));
};

const getApiUrl = () => {
  try {
    return window.RUNTIME_CONFIG?.apiUrl || process.env.REACT_APP_API_URL;
  } catch (e) {
    return process.env.REACT_APP_API_URL;
  }
};

const getMeasuredSelectWidth = (text, fallbackWidth) => {
  if (typeof document === 'undefined' || !text) {
    return fallbackWidth;
  }

  const measuringNode = document.createElement('span');
  measuringNode.style.position = 'absolute';
  measuringNode.style.visibility = 'hidden';
  measuringNode.style.whiteSpace = 'nowrap';
  measuringNode.style.fontSize = '1.6rem';
  measuringNode.style.fontWeight = '900';
  measuringNode.textContent = text;
  document.body.appendChild(measuringNode);
  const width = measuringNode.getBoundingClientRect().width;
  document.body.removeChild(measuringNode);

  return Math.ceil(width + 88);
};

const getFallbackLocationName = (position) => {
  if (!position) {
    return 'Dropped pin';
  }

  return `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`;
};

const getLocationPosition = (savedLocation) => {
  if (!savedLocation) {
    return null;
  }

  return savedLocation.location || {
    lat: savedLocation.lat,
    lng: savedLocation.lng
  };
};

const getAddressForPosition = (position) => {
  if (!position || !window.google?.maps?.Geocoder) {
    return Promise.resolve(getFallbackLocationName(position));
  }

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results?.[0]?.formatted_address) {
        resolve(results[0].formatted_address);
        return;
      }

      resolve(getFallbackLocationName(position));
    });
  });
};

const formatPreviewPledgeTime = (date, time) => {
  const pledgeTime = new Date(`${date}T${time}`);

  if (Number.isNaN(pledgeTime.getTime())) {
    return 'the pledge deadline';
  }

  return pledgeTime.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const CreateAlarm = ({ isDemo = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const locationPickerRef = useRef(null);
  const recipientPickerRef = useRef(null);
  const publicMode = isPublicApp();
  const canUseMonetaryPledges = isPersonalApp() && user?.isAdmin === true;
  const googleMapsApiKey = window.RUNTIME_CONFIG?.googleMaps?.apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [location, setLocation] = useState('');
  const [selectedLocationLabel, setSelectedLocationLabel] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mapSaving, setMapSaving] = useState(false);
  const [savedDataLoading, setSavedDataLoading] = useState(!isDemo);
  const [savedLocations, setSavedLocations] = useState([]);
  const [punishmentType, setPunishmentType] = useState(canUseMonetaryPledges ? 'monetary' : 'email');
  const [selectedContactEmails, setSelectedContactEmails] = useState([]);
  const [guestSenderName, setGuestSenderName] = useState('');
  const [savedContacts, setSavedContacts] = useState([]);
  const [accountabilityMessage, setAccountabilityMessage] = useState(isDemo ? '' : 'IOU $20 for not showing up');
  const [draftAccountabilityMessage, setDraftAccountabilityMessage] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSuccess, setPreviewSuccess] = useState('');
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const [isRecipientMenuOpen, setIsRecipientMenuOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isGuestEmailPreviewOpen, setIsGuestEmailPreviewOpen] = useState(false);
  const [saveLocationForLater, setSaveLocationForLater] = useState(publicMode);
  const [publicLocationLabel, setPublicLocationLabel] = useState('');
  const [mapLocating, setMapLocating] = useState(false);
  const [mappedLocationAddress, setMappedLocationAddress] = useState('');
  const [mappedLocationAddressLoading, setMappedLocationAddressLoading] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState('');
  const [mapSearchBox, setMapSearchBox] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 37.7749,
    lng: -122.4194
  });
  const [mapZoom, setMapZoom] = useState(14);
  const [mappedLocation, setMappedLocation] = useState(null);
  const isGuestUser = user?.isAnonymous === true;
  const selectedLocationName = getCompactAddressLabel(
    savedLocations.find(savedLocation => savedLocation.id === location)?.name || selectedLocationLabel
  ) || 'Choose destination';
  const effectivePunishmentType = canUseMonetaryPledges ? punishmentType : 'email';
  const punishmentLabel = effectivePunishmentType === 'email' ? 'send an email' : 'I pay';
  const selectedContactEmail = selectedContactEmails[0] || '';
  const selectedContactName = savedContacts.find(contact => contact.emailAddress === selectedContactEmail)?.name || 'Choose recipient';
  const locationSelectWidth = useMemo(() => {
    return getMeasuredSelectWidth(selectedLocationName, 260);
  }, [selectedLocationName]);
  const punishmentSelectWidth = useMemo(() => {
    return getMeasuredSelectWidth(punishmentLabel, 190);
  }, [punishmentLabel]);
  const contactSelectWidth = useMemo(() => {
    return getMeasuredSelectWidth(selectedContactName, 220);
  }, [selectedContactName]);
  const accountabilityMessagePreview = accountabilityMessage.trim() || 'accountability msg';
  const normalizedGuestSenderName = guestSenderName.replace(/\s+/g, ' ').trim();
  const isDemoExperience = isDemo || isGuestUser;
  const guestEmailPreview = {
    to: GUEST_SAMPLE_RECIPIENT_EMAIL,
    cc: 'you@example.com',
    senderName: normalizedGuestSenderName || 'Your name',
    subject: `${normalizedGuestSenderName || 'Your name'} missed a PlacePledge`,
    locationName: selectedLocationName === 'Choose destination' ? 'your pledge destination' : selectedLocationName,
    dueAt: formatPreviewPledgeTime(date, time),
    message: accountabilityMessage.trim() || 'Your accountability message'
  };
  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };

  useEffect(() => {
    const defaultMinutesFromNow = (isDemo || isGuestUser) ? 5 : 510;
    const defaultTime = new Date();
    defaultTime.setMinutes(defaultTime.getMinutes() + defaultMinutesFromNow);

    setDate(formatDateInputValue(defaultTime));
    setTime(defaultTime.toTimeString().slice(0, 5));
  }, [isDemo, isGuestUser]);

  useEffect(() => {
    if (publicMode || !navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setMapCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (locationError) => {
        console.error('Error getting location:', locationError);
      }
    );
  }, [publicMode]);

  useEffect(() => {
    if (!canUseMonetaryPledges) {
      setPunishmentType('email');
    }
  }, [canUseMonetaryPledges]);

  useEffect(() => {
    const handleDocumentPointerDown = (event) => {
      if (!locationPickerRef.current?.contains(event.target)) {
        setIsLocationMenuOpen(false);
      }

      if (!recipientPickerRef.current?.contains(event.target)) {
        setIsRecipientMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown);
    };
  }, []);

  useEffect(() => {
    if (isDemo) {
      return;
    }

    const fetchSavedData = async () => {
      try {
        const idToken = await user.getIdToken();
        const [locationsRes, contactsRes, alarmsRes] = await Promise.all([
          publicMode
            ? Promise.resolve({ data: [] })
            : axios.get(`${getApiUrl()}/locations`, {
              headers: { Authorization: `Bearer ${idToken}` }
            }),
          axios.get(`${getApiUrl()}/contacts`, {
            headers: { Authorization: `Bearer ${idToken}` }
          }),
          axios.get(`${getApiUrl()}/alarms`, {
            headers: { Authorization: `Bearer ${idToken}` }
          })
        ]);
        const locations = publicMode
          ? loadPublicSavedLocations().filter(isUserSavedLocation)
          : locationsRes.data.filter(isUserSavedLocation);
        const recentAlarm = getMostRecentAlarm(alarmsRes.data);

        setSavedLocations(locations);
        setSavedContacts(contactsRes.data);

        if (!recentAlarm) {
          return;
        }

        const recentLocation = locations.find(savedLocation => savedLocation.id === recentAlarm.locationId);

        if (recentLocation) {
          setLocation(recentLocation.id);
        }

        if (canUseMonetaryPledges && ['monetary', 'email'].includes(recentAlarm.punishmentType)) {
          setPunishmentType(recentAlarm.punishmentType);
        }

        if (recentAlarm.amount !== undefined && recentAlarm.amount !== null) {
          setAmount(formatAmountInput(recentAlarm.amount));
        }

        if (Array.isArray(recentAlarm.contactEmails)) {
          setSelectedContactEmails(recentAlarm.contactEmails);
        }

        if (recentAlarm.shameMessage) {
          setAccountabilityMessage(recentAlarm.shameMessage);
        }

      } catch (fetchError) {
        console.error('Error fetching saved data:', fetchError);
      } finally {
        setSavedDataLoading(false);
      }
    };

    fetchSavedData();
  }, [user, canUseMonetaryPledges, isDemo, publicMode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    if (!location || !time || !date ||
        (effectivePunishmentType === 'monetary' && !amount) ||
        (effectivePunishmentType === 'email' && ((!isDemo && !isGuestUser && !selectedContactEmails.length) || !accountabilityMessage))) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    const normalizedContactEmails = selectedContactEmails.map(emailAddress => emailAddress.trim()).filter(Boolean);

    const pledgeTime = new Date(`${date}T${time}`);

    if (Number.isNaN(pledgeTime.getTime())) {
      setError('Choose a valid pledge date and time');
      setLoading(false);
      return;
    }

    if (pledgeTime.getTime() <= Date.now()) {
      setError('Choose a future pledge time');
      setLoading(false);
      return;
    }

    const timestamp = pledgeTime.toISOString();
    const pledgeContactEmails = isGuestUser ? [GUEST_SAMPLE_RECIPIENT_EMAIL] : normalizedContactEmails;

    if (effectivePunishmentType === 'email' && isGuestUser && normalizedGuestSenderName.length < 2) {
      setError('Enter your name so the preview email has a sender');
      setLoading(false);
      return;
    }

    if (isDemo) {
      const pledgeLocation = savedLocations.find(savedLocation => savedLocation.id === location);

      saveDemoPledge({
        location: pledgeLocation,
        alarm: {
          id: 'demo-pledge',
          locationId: location,
          time: timestamp,
          active: true,
          completed: false,
          failed: false,
          punishmentType: 'email',
          contactEmails: normalizedContactEmails,
          shameMessage: accountabilityMessage,
          createdAt: new Date().toISOString()
        }
      });
      setPreviewSuccess('Demo pledge previewed. Sign in when you want to save a real pledge.');
      setLoading(false);
      navigate('/demo/dashboard');
      return;
    }

    try {
      const selectedPledgeLocation = savedLocations.find(savedLocation => savedLocation.id === location);
      const publicTargetPosition = getLocationPosition(selectedPledgeLocation);
      const fulfillmentSecret = publicMode ? createFulfillmentSecret() : null;
      const fulfillmentSecretHash = fulfillmentSecret ? await hashFulfillmentSecret(fulfillmentSecret) : null;
      const pledgeLocationLabel = normalizePublicLocationLabel(selectedPledgeLocation?.name || selectedLocationLabel);

      if (publicMode && (!publicTargetPosition || !fulfillmentSecret)) {
        throw new Error('Choose a private destination on this device');
      }

      if (publicMode) {
        const locationLabelError = getPublicLocationLabelError(pledgeLocationLabel);

        if (locationLabelError) {
          throw new Error(locationLabelError);
        }
      }

      const response = await axios.post(
        `${getApiUrl()}/alarms`,
        {
          ...(publicMode ? {
            locationLabel: pledgeLocationLabel,
            requiredDistance: selectedPledgeLocation?.requiredDistance,
            requiredDistanceValue: selectedPledgeLocation?.requiredDistanceValue,
            requiredDistanceUnit: selectedPledgeLocation?.requiredDistanceUnit,
            fulfillmentSecretHash
          } : {
            location
          }),
          time: timestamp,
          punishmentType: effectivePunishmentType,
          ...(isGuestUser ? {
            creatorIsAnonymous: true,
            metadata: {
              guestSenderName: normalizedGuestSenderName,
              emailPreviewOnly: true
            }
          } : {}),
          ...(effectivePunishmentType === 'monetary' ? {
            amount: parseFloat(amount)
          } : {
            contactEmails: pledgeContactEmails,
            shameMessage: accountabilityMessage
          })
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      if (publicMode) {
        savePublicPledgeTarget({
          pledgeId: response.data.id,
          targetLat: publicTargetPosition.lat,
          targetLng: publicTargetPosition.lng,
          radiusFeet: selectedPledgeLocation.requiredDistance,
          radiusUnit: selectedPledgeLocation.requiredDistanceUnit,
          displayLabel: pledgeLocationLabel,
          fulfillmentSecret
        });
      }

      navigate('/dashboard');
    } catch (submitError) {
      console.error('Error creating alarm:', submitError);
      setError(submitError.response?.data?.message || submitError.message || 'Failed to create pledge');
      setLoading(false);
    }
  };

  const handlePreviewEmail = async () => {
    setError('');
    setPreviewSuccess('');

    if (effectivePunishmentType !== 'email') {
      return;
    }

    if (!location || !time || !date || !accountabilityMessage) {
      setError('Choose a location, date, time, and message before sending a preview');
      return;
    }

    if (isGuestUser) {
      if (normalizedGuestSenderName.length < 2) {
        setError('Enter your name so the recipient knows who made the pledge');
        return;
      }

      setIsGuestEmailPreviewOpen(true);
      return;
    }

    if (isDemo) {
      setIsGuestEmailPreviewOpen(true);
      return;
    }

    setPreviewLoading(true);

    try {
      const timestamp = new Date(`${date}T${time}`).toISOString();
      const response = await axios.post(
        `${getApiUrl()}/alarms/email-preview`,
        {
          location,
          time: timestamp,
          shameMessage: accountabilityMessage
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      setPreviewSuccess(response.data?.message || `Preview sent to ${user.email}`);
    } catch (previewError) {
      console.error('Error sending preview email:', previewError);
      setError(previewError.response?.data?.message || 'Failed to send preview email');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSelectRecipient = (emailAddress) => {
    setSelectedContactEmails(emailAddress ? [emailAddress] : []);
    setIsRecipientMenuOpen(false);
  };

  const handleSelectLocation = (locationId) => {
    setLocation(locationId);
    setSelectedLocationLabel('');
    setIsLocationMenuOpen(false);
  };

  const handleOpenLocationMap = () => {
    setIsLocationMenuOpen(false);
    setIsRecipientMenuOpen(false);
    setIsMessageModalOpen(false);
    setIsGuestEmailPreviewOpen(false);
    setMappedLocation(null);
    setMappedLocationAddress('');
    setMappedLocationAddressLoading(false);
    setMapSearchQuery('');
    setMapSearchError('');
    setMapSearchLoading(false);
    setMapSearchBox(null);
    setSaveLocationForLater(publicMode);
    setPublicLocationLabel('');
    setMapZoom(14);
    setIsMapModalOpen(true);
  };

  const handleLocationPickerClick = () => {
    if ((isDemoExperience && !publicMode) || (publicMode && savedLocations.length === 0)) {
      handleOpenLocationMap();
      return;
    }

    setIsRecipientMenuOpen(false);
    setIsLocationMenuOpen(prev => !prev);
  };

  const handleOpenMessageModal = () => {
    setDraftAccountabilityMessage(accountabilityMessage);
    setIsLocationMenuOpen(false);
    setIsRecipientMenuOpen(false);
    setIsGuestEmailPreviewOpen(false);
    setIsMessageModalOpen(true);
  };

  const handleConfirmMessage = () => {
    setAccountabilityMessage(draftAccountabilityMessage);
    setIsMessageModalOpen(false);
  };

  const handleMapClick = async (event) => {
    if (!event?.latLng) {
      return;
    }

    const nextLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    setMappedLocation(nextLocation);
    setMappedLocationAddress('');
    setPublicLocationLabel('');
    setMappedLocationAddressLoading(true);
    setMapSearchError('');
    setMapCenter(nextLocation);

    const address = await getAddressForPosition(nextLocation);
    setMappedLocationAddress(address);
    setMappedLocationAddressLoading(false);
  };

  const handleMapSearch = () => {
    const query = mapSearchQuery.trim();

    if (!query) {
      setMapSearchError('Enter an address');
      return;
    }

    if (!window.google?.maps?.Geocoder) {
      setMapSearchError('Address search is still loading');
      return;
    }

    setMapSearchLoading(true);
    setMapSearchError('');

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      setMapSearchLoading(false);

      const result = results?.[0];
      const locationResult = result?.geometry?.location;

      if (status !== 'OK' || !locationResult) {
        setMapSearchError('No matching address found');
        return;
      }

      applyMapPlaceResult(result, query);
    });
  };

  const applyMapPlaceResult = (place, fallbackName = '') => {
    const locationResult = place?.geometry?.location;

    if (!locationResult) {
      setMapSearchError('No matching address found');
      return;
    }

    const nextLocation = {
      lat: locationResult.lat(),
      lng: locationResult.lng()
    };
    const nextAddress = place.formatted_address || place.name || fallbackName;

    setMappedLocation(nextLocation);
    setMappedLocationAddress(nextAddress);
    setPublicLocationLabel(publicMode ? getSafePlaceNameSuggestion(place) : '');
    setMappedLocationAddressLoading(false);
    setMapSearchQuery(nextAddress);
    setMapSearchError('');
    setMapCenter(nextLocation);
    setMapZoom(17);
  };

  const handleMapPlacesChanged = () => {
    const places = mapSearchBox?.getPlaces();
    const place = places?.[0];

    if (!place) {
      return;
    }

    applyMapPlaceResult(place, mapSearchQuery.trim());
  };

  const handleMapSearchKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleMapSearch();
  };

  const handleCenterOnCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Current location is not available in this browser');
      return;
    }

    setMapLocating(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setMapCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setMapZoom(17);
        setMapLocating(false);
      },
      (locationError) => {
        console.error('Error getting location:', locationError);
        setError('Unable to get your current location');
        setMapLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000
      }
    );
  };

  const handleSetMappedLocation = async () => {
    if (!mappedLocation) {
      setError('Search for an address or tap the map to drop a destination pin');
      return;
    }

    setMapSaving(true);
    setError('');

    if (isDemo) {
      const defaultRadius = loadDefaultLocationRadius();
      const locationName = mappedLocationAddress || await getAddressForPosition(mappedLocation);
      const nextLocation = {
        id: `demo-pin-${mappedLocation.lat.toFixed(5)}-${mappedLocation.lng.toFixed(5)}`,
        name: locationName,
        location: mappedLocation,
        requiredDistance: defaultRadius.requiredDistance,
        requiredDistanceValue: Number(defaultRadius.value),
        requiredDistanceUnit: defaultRadius.unit
      };

      setSavedLocations(prevLocations => [
        ...prevLocations.filter(savedLocation => savedLocation.id !== nextLocation.id),
        nextLocation
      ]);
      setLocation(nextLocation.id);
      setSelectedLocationLabel(getCompactAddressLabel(nextLocation.name || locationName));
      setIsMapModalOpen(false);
      setMappedLocation(null);
      setMapSaving(false);
      return;
    }

    if (publicMode) {
      const defaultRadius = loadDefaultLocationRadius();
      const locationName = mappedLocationAddress || await getAddressForPosition(mappedLocation);
      const locationLabel = normalizePublicLocationLabel(publicLocationLabel);
      const locationLabelError = getPublicLocationLabelError(locationLabel);

      if (locationLabelError) {
        setMapSearchError(locationLabelError);
        setMapSaving(false);
        return;
      }

      const nextLocation = {
        id: `public-pin-${mappedLocation.lat.toFixed(5)}-${mappedLocation.lng.toFixed(5)}`,
        name: locationLabel,
        address: locationName,
        location: mappedLocation,
        requiredDistance: defaultRadius.requiredDistance,
        requiredDistanceValue: Number(defaultRadius.value),
        requiredDistanceUnit: defaultRadius.unit,
        localOnly: true
      };

      if (saveLocationForLater) {
        const nextSavedLocations = savePublicSavedLocation(nextLocation);
        setSavedLocations(nextSavedLocations.filter(isUserSavedLocation));
      } else {
        setSavedLocations(prevLocations => [
          ...prevLocations.filter(savedLocation => savedLocation.id !== nextLocation.id),
          nextLocation
        ]);
      }
      setLocation(nextLocation.id);
      setSelectedLocationLabel(locationLabel);
      setIsMapModalOpen(false);
      setMappedLocation(null);
      setMapSaving(false);
      return;
    }

    try {
      const defaultRadius = loadDefaultLocationRadius();
      const locationName = mappedLocationAddress || await getAddressForPosition(mappedLocation);
      const response = await axios.post(
        `${getApiUrl()}/locations`,
        {
          location: mappedLocation,
          name: locationName,
          requiredDistance: defaultRadius.requiredDistance,
          requiredDistanceValue: Number(defaultRadius.value),
          requiredDistanceUnit: defaultRadius.unit,
          ...(!saveLocationForLater ? {
            temporary: true,
            source: 'create_pledge'
          } : {})
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );
      const nextLocation = response.data;

      if (saveLocationForLater) {
        setSavedLocations(prevLocations => [
          ...prevLocations.filter(savedLocation => savedLocation.id !== nextLocation.id),
          nextLocation
        ]);
      }

      setLocation(nextLocation.id);
      setSelectedLocationLabel(getCompactAddressLabel(nextLocation.name || locationName));
      setIsMapModalOpen(false);
      setMappedLocation(null);
    } catch (mapError) {
      console.error('Error setting mapped location:', mapError);
      setError(mapError.response?.data?.message || 'Failed to set location');
    } finally {
      setMapSaving(false);
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo src={placePledgeLogo} alt="Place Pledge" />
              <HeaderIconButton as={Link} to={isDemo ? '/login' : '/dashboard'} aria-label="Home">
                <HomeIcon aria-hidden="true" />
              </HeaderIconButton>
            </BrandHeaderRow>
          </HeaderText>
        </Header>

        <Card onSubmit={handleSubmit}>
          <MenuScrim
            $open={isLocationMenuOpen || isRecipientMenuOpen}
            type="button"
            aria-label="Close menu"
            onClick={() => {
              setIsLocationMenuOpen(false);
              setIsRecipientMenuOpen(false);
            }}
          />
          <PledgePanel>
            <PledgeText>
              <PledgeWords>I pledge to be at</PledgeWords>
              <InlineControl>
                {savedDataLoading && !isDemoExperience ? (
                  <LoadingRow>
                    <Spinner />
                    Loading your places
                  </LoadingRow>
                ) : (
                  <RecipientPicker ref={locationPickerRef} $active={isLocationMenuOpen}>
                    <RecipientButton
                      type="button"
                      aria-haspopup={isDemoExperience && !(publicMode && savedLocations.length > 0) ? 'dialog' : 'listbox'}
                      aria-expanded={isLocationMenuOpen}
                      onClick={handleLocationPickerClick}
                      style={{ '--select-width': `${locationSelectWidth}px` }}
                    >
                      {selectedLocationName}
                    </RecipientButton>
                    <RecipientMenu $open={isLocationMenuOpen} role="listbox">
                      {savedLocations.map(savedLocation => (
                        <RecipientOption
                          key={savedLocation.id}
                          type="button"
                          role="option"
                          aria-selected={location === savedLocation.id}
                          onClick={() => handleSelectLocation(savedLocation.id)}
                        >
                          {getCompactAddressLabel(savedLocation.name) || savedLocation.name}
                        </RecipientOption>
                      ))}
                      <RecipientOption
                        $manage
                        type="button"
                        role="option"
                        onClick={handleOpenLocationMap}
                      >
                        New Location
                      </RecipientOption>
                    </RecipientMenu>
                  </RecipientPicker>
                )}
              </InlineControl>

              <PledgeWords>by</PledgeWords>
              <TimeGrid>
                <Field>
                  <VisuallyHiddenText>Wake date</VisuallyHiddenText>
                  <DateInputShell>
                    <DateDisplay aria-hidden="true">{formatDateDisplayLabel(date)}</DateDisplay>
                    <DatePickerInput
                      type="date"
                      value={date}
                      aria-label="Wake date"
                      onChange={(event) => setDate(event.target.value)}
                      required
                    />
                  </DateInputShell>
                </Field>
                <TimeSeparator>@</TimeSeparator>
                <Field>
                  <VisuallyHiddenText>Wake time</VisuallyHiddenText>
                  <SentenceInput
                    type="time"
                    value={time}
                    onChange={(event) => setTime(event.target.value)}
                    required
                  />
                </Field>
              </TimeGrid>

              <PledgeWords>or else</PledgeWords>
              {effectivePunishmentType === 'monetary' ? (
                <InlineControl>
                  <MoneyRow>
                    <SentenceSelect
                      value={punishmentType}
                      onChange={(event) => setPunishmentType(event.target.value)}
                      style={{ '--select-width': `${punishmentSelectWidth}px` }}
                    >
                      <option value="monetary">I pay</option>
                      <option value="email">send an email</option>
                    </SentenceSelect>

                    <AmountSlider>
                      <AmountSliderHeader>
                        <span>Amount</span>
                        <AmountSliderValue>${amount}</AmountSliderValue>
                      </AmountSliderHeader>
                      <RangeInput
                        type="range"
                        min={AMOUNT_MIN}
                        max={AMOUNT_MAX}
                        step="1"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                        onInput={(event) => setAmount(event.target.value)}
                        aria-label="Pledge amount"
                        required
                      />
                    </AmountSlider>
                  </MoneyRow>
                </InlineControl>
              ) : (
                <>
                  {canUseMonetaryPledges && (
                    <InlineControl>
                      <SentenceSelect
                        value={punishmentType}
                        onChange={(event) => setPunishmentType(event.target.value)}
                        style={{ '--select-width': `${punishmentSelectWidth}px` }}
                      >
                        <option value="monetary">I pay</option>
                        <option value="email">send an email</option>
                      </SentenceSelect>
                    </InlineControl>
                  )}
                  {!canUseMonetaryPledges && <PledgeWords>{isDemoExperience ? 'send an email to' : 'send an email'}</PledgeWords>}
                  {!isDemoExperience && <PledgeWords>to</PledgeWords>}
                  <InlineControl>
                    {isDemoExperience ? (
                      <DemoRecipientChip
                        aria-label={`Sample recipient: ${GUEST_SAMPLE_RECIPIENT_EMAIL}. No email is sent in demo mode.`}
                      >
                        {GUEST_SAMPLE_RECIPIENT_EMAIL}
                      </DemoRecipientChip>
                    ) : (
                      <RecipientPicker ref={recipientPickerRef} $active={isRecipientMenuOpen}>
                        <RecipientButton
                          type="button"
                          aria-haspopup="listbox"
                          aria-expanded={isRecipientMenuOpen}
                          onClick={() => {
                            setIsLocationMenuOpen(false);
                            setIsRecipientMenuOpen(prev => !prev);
                          }}
                          style={{ '--select-width': `${contactSelectWidth}px` }}
                        >
                          {selectedContactName}
                        </RecipientButton>
                        <RecipientMenu $open={isRecipientMenuOpen} role="listbox">
                          {savedContacts.map(contact => (
                            <RecipientOption
                              key={contact.id}
                              type="button"
                              role="option"
                              aria-selected={selectedContactEmail === contact.emailAddress}
                              disabled={!contact.emailAddress}
                              onClick={() => handleSelectRecipient(contact.emailAddress)}
                            >
                              {contact.name}{contact.consentStatus && contact.consentStatus !== 'accepted' ? ` (${contact.consentStatus})` : ''}
                            </RecipientOption>
                          ))}
                          <RecipientOption
                            $manage
                            type="button"
                            role="option"
                            onClick={() => navigate('/manage-contacts')}
                          >
                            Manage contacts
                          </RecipientOption>
                        </RecipientMenu>
                      </RecipientPicker>
                    )}
                  </InlineControl>
                  {isGuestUser && (
                    <>
                      <PledgeWords>from</PledgeWords>
                      <InlineControl>
                        <SenderNameInput
                          type="text"
                          value={guestSenderName}
                          placeholder="Your name"
                          aria-label="Your name shown in email"
                          maxLength="60"
                          onChange={(event) => setGuestSenderName(event.target.value)}
                          required
                        />
                      </InlineControl>
                    </>
                  )}
                  <MessagePhrase>
                    <PledgeWords>saying</PledgeWords>
                    <MessagePreviewButton
                      type="button"
                      $empty={!accountabilityMessage.trim()}
                      aria-label="Edit email message"
                      onClick={handleOpenMessageModal}
                    >
                      <span>{accountabilityMessagePreview}</span>
                    </MessagePreviewButton>
                  </MessagePhrase>
                </>
              )}
            </PledgeText>
          </PledgePanel>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {previewSuccess && <SuccessMessage>{previewSuccess}</SuccessMessage>}

          <FormActions>
            <SubmitButton type="submit" disabled={loading || !location}>
              {loading ? 'Sealing...' : 'Seal My Pledge'}
            </SubmitButton>
            {effectivePunishmentType === 'email' && (
              <PreviewButton
                type="button"
                disabled={!isGuestUser && previewLoading}
                onClick={handlePreviewEmail}
              >
                {isDemoExperience ? 'Preview email' : previewLoading ? 'Sending...' : 'Send me a preview email'}
              </PreviewButton>
            )}
          </FormActions>
        </Card>
        {isMessageModalOpen && (
          <MessageModalBackdrop>
            <MessageModal role="dialog" aria-modal="true" aria-labelledby="message-modal-title">
              <MessageModalTitle id="message-modal-title">Message preview</MessageModalTitle>
              <SentenceTextArea
                value={draftAccountabilityMessage}
                placeholder="if you see this email, i skipped the gym and i owe you $10."
                aria-label="Email message"
                onChange={(event) => setDraftAccountabilityMessage(event.target.value)}
                autoFocus
                required
              />
              <MessageModalActions>
                <MapButton
                  $secondary
                  type="button"
                  onClick={() => setIsMessageModalOpen(false)}
                >
                  Cancel
                </MapButton>
                <MapButton
                  type="button"
                  onClick={handleConfirmMessage}
                >
                  OK
                </MapButton>
              </MessageModalActions>
            </MessageModal>
          </MessageModalBackdrop>
        )}
        {isGuestEmailPreviewOpen && (
          <MessageModalBackdrop>
            <MessageModal role="dialog" aria-modal="true" aria-labelledby="guest-email-preview-title">
              <MessageModalTitle id="guest-email-preview-title">Email preview</MessageModalTitle>
              <EmailPreviewShell>
                <EmailPreviewMeta>
                  <div><strong>To:</strong> {guestEmailPreview.to}</div>
                  <div><strong>Cc:</strong> {guestEmailPreview.cc}</div>
                  <div><strong>Subject:</strong> {guestEmailPreview.subject}</div>
                </EmailPreviewMeta>
                <EmailPreviewCard>
                  <EmailPreviewHeader>
                    <EmailPreviewBrand>PlacePledge</EmailPreviewBrand>
                    <EmailPreviewHeadline>{guestEmailPreview.senderName} missed a PlacePledge</EmailPreviewHeadline>
                  </EmailPreviewHeader>
                  <EmailPreviewBody>
                    <p>They precommitted to send this accountability note if they missed their pledge.</p>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>The pledge</EmailPreviewLabel>
                      <EmailPreviewPrimary>Be at {guestEmailPreview.locationName}</EmailPreviewPrimary>
                      <p>by {guestEmailPreview.dueAt}</p>
                    </EmailPreviewBlock>
                    <EmailPreviewBlock>
                      <EmailPreviewLabel>Their message</EmailPreviewLabel>
                      <EmailPreviewMessage>{guestEmailPreview.message}</EmailPreviewMessage>
                    </EmailPreviewBlock>
                    <p>PlacePledge helps people make location-based promises with real consequences.</p>
                  </EmailPreviewBody>
                  <EmailPreviewFooter>
                    This is a sample preview. No email is sent while you are using PlacePledge as a guest.
                  </EmailPreviewFooter>
                </EmailPreviewCard>
              </EmailPreviewShell>
              <MessageModalActions>
                <MapButton
                  type="button"
                  onClick={() => setIsGuestEmailPreviewOpen(false)}
                >
                  OK
                </MapButton>
              </MessageModalActions>
            </MessageModal>
          </MessageModalBackdrop>
        )}
        {isMapModalOpen && (
          <MapModalBackdrop>
            <MapModal role="dialog" aria-modal="true" aria-label="Set pledge destination">
              {googleMapsApiKey ? (
                <LoadScript
                  googleMapsApiKey={googleMapsApiKey}
                  version="quarterly"
                  libraries={GOOGLE_MAPS_LIBRARIES}
                >
                  <MapSearchPanel>
                    <MapSearchRow>
                      <StandaloneSearchBox
                        onLoad={setMapSearchBox}
                        onUnmount={() => setMapSearchBox(null)}
                        onPlacesChanged={handleMapPlacesChanged}
                      >
                        <MapSearchInput
                          type="search"
                          value={mapSearchQuery}
                          placeholder="Search for an address"
                          aria-label="Search for an address"
                          onChange={(event) => {
                            setMapSearchQuery(event.target.value);
                            setMapSearchError('');
                          }}
                          onKeyDown={handleMapSearchKeyDown}
                        />
                      </StandaloneSearchBox>
                      <MapSearchButton
                        type="button"
                        disabled={mapSearchLoading}
                        onClick={handleMapSearch}
                      >
                        {mapSearchLoading ? 'Searching...' : 'Search'}
                      </MapSearchButton>
                    </MapSearchRow>
                    {mapSearchError && <MapSearchError>{mapSearchError}</MapSearchError>}
                  </MapSearchPanel>
                  <MapLocateButton
                    type="button"
                    disabled={mapLocating}
                    aria-label="Center map on current location"
                    title="Center map on current location"
                    onClick={handleCenterOnCurrentLocation}
                  >
                    <CompassIcon />
                  </MapLocateButton>
                  <MapContainer>
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={mapZoom}
                      onClick={handleMapClick}
                    >
                      {mappedLocation && <Marker position={mappedLocation} />}
                    </GoogleMap>
                  </MapContainer>
                </LoadScript>
              ) : (
                <>
                  <MapHint>
                    Google Maps API key is not configured.
                  </MapHint>
                  <MapContainer />
                </>
              )}
              <MapOverlayActions>
                <MapSelectionDetails>
                  {mappedLocation && (
                    <SelectedAddress>
                      {mappedLocationAddressLoading ? 'Looking up address...' : mappedLocationAddress}
                    </SelectedAddress>
                  )}
                  {publicMode && mappedLocation && (
                    <PublicLocationLabelField>
                      <PublicLocationLabelText>Label this destination</PublicLocationLabelText>
                      <PublicLocationLabelInput
                        type="text"
                        value={publicLocationLabel}
                        maxLength={40}
                        placeholder="Gym"
                        aria-label="Destination label"
                        onChange={(event) => {
                          setPublicLocationLabel(event.target.value);
                          setMapSearchError('');
                        }}
                      />
                      <PublicLocationLabelHint>
                        Use a simple label. This will appear in the accountability email.
                      </PublicLocationLabelHint>
                    </PublicLocationLabelField>
                  )}
                  {!isDemo && mappedLocation && (publicMode || !isGuestUser) && (
                    <SaveForLaterField>
                      <SaveForLaterLabel>
                        <input
                          type="checkbox"
                          checked={saveLocationForLater}
                          onChange={(event) => setSaveLocationForLater(event.target.checked)}
                        />
                        {publicMode ? 'Save location' : 'Save for later'}
                      </SaveForLaterLabel>
                      {publicMode && (
                        <SaveForLaterHint>
                          For privacy, this location is saved only in this browser on this device.
                        </SaveForLaterHint>
                      )}
                    </SaveForLaterField>
                  )}
                </MapSelectionDetails>
                <MapActionButtons>
                  <MapButton
                    $secondary
                    type="button"
                    onClick={() => setIsMapModalOpen(false)}
                  >
                    Cancel
                  </MapButton>
                  <MapButton
                    type="button"
                    disabled={mapSaving || !mappedLocation || !googleMapsApiKey || (publicMode && !normalizePublicLocationLabel(publicLocationLabel))}
                    onClick={handleSetMappedLocation}
                  >
                    {mapSaving ? 'Setting...' : 'Set'}
                  </MapButton>
                </MapActionButtons>
              </MapOverlayActions>
            </MapModal>
          </MapModalBackdrop>
        )}
      </Shell>
    </Page>
  );
};

export default CreateAlarm;
